import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import {
  clearAllGatedDownloadCookies,
  GATED_DOWNLOAD_RESET_QUERY_PARAM,
  hasGatedDownloadCookie,
  setGatedDownloadCookie,
} from '#/lib/gated-download-cookie';
import type { SanityGatedContentDownload } from '#/lib/sanity/queries/gatedContentDownload';
import { cn } from '#/lib/utils';
import { BasicText } from '#/components/BasicText';
import { Button } from '#/components/Button';
import { FormErrorMessage } from '#/components/Form/Custom/ErrorMessage';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '#/components/Form/FormControls';
import { Form } from '#/components/Form/Single';
import { addBusinessEmailValidation } from '#/components/Form/utils';
import LoadingSpinner from '#/components/LoadingSpinner';
import { Media } from '#/components/Media';

/** Sanity CDN needs `dl` so the browser downloads instead of navigating to the file. */
function resolveDownloadHref(url: string): string {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.endsWith('cdn.sanity.io')) {
      return url;
    }
    if (!parsed.searchParams.has('dl')) {
      parsed.searchParams.set('dl', '');
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

function DownloadButton({
  href,
  label,
  className,
}: {
  href: string;
  label: string;
  className?: string;
}) {
  return (
    <Button
      href={resolveDownloadHref(href)}
      variant="primary"
      theme="dark"
      className={cn('w-full @[760px]:w-auto', className)}
    >
      {label}
    </Button>
  );
}

/**
 * Purple inline gated-download card for blog / resource rich text.
 * Email form until unlocked; cookie (form id + download URL) skips the form
 * on return visits.
 *
 * Append `?resetGatedDownload` to clear unlock cookies and show the form again.
 *
 * Layout keys off the card width via `@container`. Side-by-side only at
 * ≥760px so the blog column stacks before the form column gets cramped.
 */
export function GatedContentDownload(props: SanityGatedContentDownload) {
  const router = useRouter();
  const formId = props.form._id;
  const downloadUrl = props.downloadUrl;
  const downloadLabel = props.downloadButtonLabel ?? 'Download';
  const formFieldsSchema = addBusinessEmailValidation(props.form);

  const [unlocked, setUnlocked] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const shouldReset =
      router.query[GATED_DOWNLOAD_RESET_QUERY_PARAM] !== undefined;

    if (shouldReset) {
      clearAllGatedDownloadCookies();
      setUnlocked(false);

      const nextQuery = { ...router.query };
      delete nextQuery[GATED_DOWNLOAD_RESET_QUERY_PARAM];
      void router.replace(
        { pathname: router.pathname, query: nextQuery },
        undefined,
        { shallow: true },
      );
    } else {
      setUnlocked(hasGatedDownloadCookie(formId, downloadUrl));
    }

    setHydrated(true);
  }, [formId, downloadUrl, router]);

  const unlock = () => {
    setGatedDownloadCookie(formId, downloadUrl);
    setUnlocked(true);
  };

  return (
    <div className="@container my-50 m:my-60">
      <div
        className={cn(
          'flex flex-col gap-24 rounded-16 bg-cipher px-20 py-24 text-cloud',
          '@[760px]:flex-row @[760px]:items-center @[760px]:gap-32 @[760px]:px-40 @[760px]:py-32',
        )}
      >
        <div className="w-full shrink-0 @[760px]:w-[42%]">
          <Media
            media={props.media}
            className="w-full overflow-hidden rounded-8 shadow-[1.3px_1.3px_6.5px_0_rgba(67,66,184,0.3)]"
            mediaClassName="object-cover w-full"
            sizes={['100vw', { m: '360px' }]}
          />
        </div>

        <div className="flex min-w-0 w-full flex-1 flex-col gap-24">
          <div className="flex flex-col gap-20">
            <h3 className="text-pretty text-26 font-[400] leading-120 -tracking-2 @[760px]:text-30">
              {props.headline}
            </h3>
            <p className="text-pretty text-16 font-light leading-150 text-cloud !mt-0">
              {props.body}
            </p>
          </div>

          {!hydrated ? (
            <div
              className="h-50 w-full max-w-600 rounded-10 bg-cloud/20"
              aria-hidden
            />
          ) : unlocked ? (
            <div className="w-full max-w-600">
              <DownloadButton href={downloadUrl} label={downloadLabel} />
            </div>
          ) : (
            <div className="w-full max-w-600">
              <Form
                formTitle={props.form.formTitle}
                hubspotCampaignId={props.form.hubspotCampaignId}
                hubspotConvertingFormType={props.form.hubspotConvertingFormType}
                contentPieceTitle={props.form.contentPieceTitle}
                hubspotEmailSubscriptionIds={
                  props.form.hubspotEmailSubscriptionIds
                }
                formFieldsSchema={formFieldsSchema}
                trackingEventName={props.form.trackingEventName}
                trackingEventProperties={props.form.trackingEventProperties}
                onSubmitSuccess={unlock}
                ConfirmationComponent={() => (
                  <DownloadButton href={downloadUrl} label={downloadLabel} />
                )}
              >
                {({ control, formState }) => (
                  <div>
                    <div
                      className={cn(
                        'flex flex-col gap-10',
                        '@[420px]:form-input @[420px]:flex-row @[420px]:items-stretch @[420px]:gap-0 @[420px]:overflow-clip @[420px]:p-0',
                        '@[420px]:focus-within:border-grayscale-03',
                      )}
                    >
                      <FormField
                        name="email"
                        control={control}
                        defaultValue=""
                        render={({ field: fieldProps }) => (
                          <FormItem className="min-w-0 flex-1">
                            <FormLabel className="sr-only">
                              Work email
                            </FormLabel>
                            <FormControl>
                              <input
                                type="email"
                                autoComplete="work email"
                                required
                                placeholder="Enter your work email"
                                className={cn(
                                  'form-input h-50 w-full px-16 text-12 font-light text-charcoal placeholder:text-grayscale-04',
                                  'focus:outline-none focus-visible:outline-none focus-visible:border-grayscale-03',
                                  '@[420px]:rounded-none @[420px]:border-none @[420px]:bg-transparent',
                                  '@[420px]:focus:outline-none @[420px]:focus-visible:border-none autofill-override',
                                )}
                                {...fieldProps}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <Button
                        variant="form"
                        formTheme="dark"
                        type="submit"
                        disabled={formState.isSubmitting}
                        className={cn(
                          'relative grid w-full min-w-120 shrink-0 place-items-center border-none',
                          '@[420px]:w-auto @[420px]:rounded-none',
                          formState.isSubmitting && 'bg-electric-06',
                        )}
                      >
                        <span
                          className={cn(
                            'col-start-1 row-start-1',
                            formState.isSubmitting && 'opacity-0',
                          )}
                        >
                          {props.form.submitButtonLabel || 'Submit'}
                        </span>
                        {formState.isSubmitting ? (
                          <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                            <LoadingSpinner
                              formTheme="dark"
                              className="size-18"
                            />
                          </span>
                        ) : null}
                      </Button>
                    </div>
                    <FormField
                      name="email"
                      control={control}
                      render={() => (
                        <FormMessage className="legal-text mt-8 text-electric" />
                      )}
                    />
                    <FormErrorMessage formState={formState} className="mt-8" />
                    {props.form.legalText && props.form.legalText.length > 0 ? (
                      <BasicText
                        className="legal-text mt-10 font-light text-cloud/80"
                        value={props.form.legalText}
                      />
                    ) : null}
                  </div>
                )}
              </Form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
